import { useState, type ReactNode } from "react";
import { Send } from "lucide-react";
import type { ListingSellerPrequalificationDto, PrequalInviteDto } from "@/api/buyers";
import {
  Button,
  Input,
  StatusBadge,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import {
  useListingSellerPrequalification,
  useListingSellerPrequalificationInvites,
  useResendListingSellerPrequalInvite,
  useResendSellerPrequalInvite,
  useSellerPrequalification,
  useSellerPrequalificationInvites,
  useSendListingSellerPrequalInvite,
  useSendSellerPrequalInvite,
  useUnlinkListingSellerPrequalification,
  useUnlinkSellerPrequalification,
} from "@/hooks/buyers/useSellerPrequalification";
import { SellerPrequalificationCandidateModal } from "../SellerPrequalificationCandidateModal";
import { DetailField, DetailFieldList } from "../detailBits";
import { SendPreviewModal } from "../sendPreview/SendPreviewModal";
import { InvitationsSection, SubjectDuplicateConfirm } from "./InvitationsSection";
import { PrequalificationSurface } from "./PrequalificationSurface";

/**
 * The SELLER half of the shared prequalification surface, and the two adapters
 * that reach it.
 *
 * <p>🔴 <b>ONE component, TWO addresses, and the addresses are not
 * interchangeable.</b> The LISTING is the primary one — a seller
 * prequalification describes the seller and the business, so it is a fact about
 * the listing, and the listing is where the surface that manages it lives. The
 * SUBMISSION address stays live because a specialist standing on a submission is
 * genuinely where an invitation came from, the invitation records that, and —
 * load-bearing — the two carry DIFFERENT authorization gates. A
 * submission-addressed write takes the buyer's own per-row ownership rule; a
 * listing-addressed one takes <c>Listings.Manage</c> with no row check, which is
 * that controller's own documented rule. Resolving one address into the other
 * would let a specialist who does not own the buyer send a seller invitation
 * from a submission surface, as a side effect of a UI change rather than as a
 * decision anyone made.</p>
 *
 * <p>So the SUBJECT is shared and the ADDRESS is an adapter. Both mounts render
 * the same rows, the same lifecycle vocabulary and the same three-state read;
 * only the endpoints and the gate differ, and each adapter is a hook so the
 * rules-of-hooks stay honest.</p>
 */

/** What the surface needs, whichever record supplied it. */
export interface SellerPrequalAdapter {
  /** For the picker and the invite composer. */
  listingId: string | undefined;
  status: {
    data: ListingSellerPrequalificationDto | undefined;
    loading: boolean;
    error: boolean;
    retrying: boolean;
    refetch: () => void;
  };
  invites: {
    rows: PrequalInviteDto[];
    loading: boolean;
    error: boolean;
    retrying: boolean;
    refetch: () => void;
  };
  send: (payload: {
    email: string;
    firstName: string | null;
    lastName: string | null;
    confirmDuplicate: boolean;
  }) => Promise<unknown>;
  sending: boolean;
  resend: (inviteId: string, cb: { onSuccess: () => void; onError: (e: unknown) => void }) => void;
  resending: boolean;
  unlink: (cb: { onSuccess: () => void; onError: (e: unknown) => void }) => void;
  unlinking: boolean;
  /** Which id the candidate picker addresses. Exactly one is set. */
  picker: { submissionId?: string; listingId?: string };
}

/** Addressed by the SUBMISSION a specialist is standing on. */
export function useSellerPrequalAdapterForSubmission(
  submissionId: string,
): SellerPrequalAdapter {
  const status = useSellerPrequalification(submissionId);
  const invites = useSellerPrequalificationInvites(submissionId);
  const send = useSendSellerPrequalInvite(submissionId);
  const resend = useResendSellerPrequalInvite(submissionId);
  const unlink = useUnlinkSellerPrequalification(submissionId);

  return {
    listingId: status.data?.listingId,
    status: {
      data: status.data,
      loading: status.isLoading,
      error: status.isError,
      retrying: status.isFetching,
      refetch: () => void status.refetch(),
    },
    invites: {
      rows: invites.data?.data ?? [],
      loading: invites.isLoading,
      error: invites.isError,
      retrying: invites.isFetching,
      refetch: () => void invites.refetch(),
    },
    send: (payload) => send.mutateAsync(payload),
    sending: send.isPending,
    resend: (inviteId, cb) => resend.mutate(inviteId, cb),
    resending: resend.isPending,
    unlink: (cb) => unlink.mutate(undefined, cb),
    unlinking: unlink.isPending,
    picker: { submissionId },
  };
}

/** Addressed by the LISTING that owns the fact. */
export function useSellerPrequalAdapterForListing(listingId: string): SellerPrequalAdapter {
  const status = useListingSellerPrequalification(listingId);
  const invites = useListingSellerPrequalificationInvites(listingId);
  const send = useSendListingSellerPrequalInvite(listingId);
  const resend = useResendListingSellerPrequalInvite(listingId);
  const unlink = useUnlinkListingSellerPrequalification(listingId);

  return {
    listingId,
    status: {
      data: status.data,
      loading: status.isLoading,
      error: status.isError,
      retrying: status.isFetching,
      refetch: () => void status.refetch(),
    },
    invites: {
      rows: invites.data?.data ?? [],
      loading: invites.isLoading,
      error: invites.isError,
      retrying: invites.isFetching,
      refetch: () => void invites.refetch(),
    },
    send: (payload) => send.mutateAsync(payload),
    sending: send.isPending,
    resend: (inviteId, cb) => resend.mutate(inviteId, cb),
    resending: resend.isPending,
    unlink: (cb) => unlink.mutate(undefined, cb),
    unlinking: unlink.isPending,
    picker: { listingId },
  };
}

export function SellerPrequalificationSurface({
  adapter,
  canEdit,
  intro,
  testId = "seller-prequalification",
}: {
  adapter: SellerPrequalAdapter;
  canEdit: boolean;
  /** Copy the ADDRESS wants to state about itself. The submission mount uses it
   *  to say that this belongs to the listing and reaches every other buyer. */
  intro?: ReactNode;
  testId?: string;
}) {
  const toast = useToast();
  const [picking, setPicking] = useState(false);

  const l = adapter.status.data;
  const linked = Boolean(l?.submissionId);

  return (
    <>
      <PrequalificationSurface
        subject="seller"
        canEdit={canEdit}
        testId={testId}
        intro={intro}
        status={{
          loading: adapter.status.loading,
          error: adapter.status.error,
          retrying: adapter.status.retrying,
          onRetry: adapter.status.refetch,
          linked,
          body: l ? <LinkedSellerFacts link={l} /> : null,
        }}
        link={{
          onChange: () => setPicking(true),
          onUnlink: () =>
            adapter.unlink({
              onSuccess: () => toast.success("Seller prequalification unlinked."),
              onError: (err) =>
                toast.error(
                  (err as { message?: string })?.message ?? "Could not unlink it."),
            }),
          unlinking: adapter.unlinking,
        }}
        invitations={<SellerInvitations adapter={adapter} canEdit={canEdit} />}
      />

      {/* REUSED, never reimplemented. Its discipline is the point: it surfaces
          when another listing already claims a submission, requires a confirm,
          and never auto-links. */}
      <SellerPrequalificationCandidateModal
        {...adapter.picker}
        open={picking}
        onClose={() => setPicking(false)}
      />
    </>
  );
}

/** The linked prequalification's own facts. Deliberately NOT the outcome alone:
 *  a reference and a date are what let a specialist recognise it as the one they
 *  were expecting. */
function LinkedSellerFacts({ link: l }: { link: ListingSellerPrequalificationDto }) {
  return (
    <>
      <div className="mb-space-3">
        <StatusBadge tone={l.outcome === "Pass" ? "ok" : "warn"}>
          {l.outcome === "Pass" ? "Prequalified" : l.outcome}
        </StatusBadge>
      </div>

      <DetailFieldList>
        <DetailField label="Reference" value={l.referenceNumber} />
        <DetailField
          label="Submitted"
          value={l.submittedAtUtc ? <Timestamp value={l.submittedAtUtc} /> : null}
        />
        <DetailField
          label="Linked"
          value={l.linkedAtUtc ? <Timestamp value={l.linkedAtUtc} /> : null}
        />
      </DetailFieldList>
    </>
  );
}

/**
 * The seller's invitations, and the composer that is deliberately not shared
 * with the buyer's.
 *
 * <p>🔴 <b>Email is required and has NO default.</b> The buyer form falls back to
 * the buyer's own record; this module holds no seller contact record at all, so
 * asking is the only honest option — and because the address is hand-typed and
 * unverified, the send goes through the preview first. That is the whole reason
 * the composer is a slot rather than shared code.</p>
 */
function SellerInvitations({
  adapter,
  canEdit,
}: {
  adapter: SellerPrequalAdapter;
  canEdit: boolean;
}) {
  const toast = useToast();

  const [composing, setComposing] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  async function onSend(confirmDuplicate = false) {
    const address = email.trim();
    if (!address) return;

    try {
      await adapter.send({
        email: address,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        confirmDuplicate,
      });
      setPreviewing(false);
      setComposing(false);
      setConfirmingDuplicate(false);
      setEmail("");
      setFirstName("");
      setLastName("");
      toast.success("Invitation sent.");
    } catch (err) {
      // A live invitation already exists for this address. Surfaced as a
      // confirm rather than swallowed — the sender may genuinely want a second
      // one, but never by accident.
      //
      // Raised back on the FORM, not inside the preview: "send anyway" is a
      // decision about a different invitation, and answering it from behind a
      // preview of this one would be answering the wrong question.
      if ((err as { status?: number })?.status === 409) {
        setPreviewing(false);
        setConfirmingDuplicate(true);
        return;
      }
      toast.error(
        (err as { message?: string })?.message ?? "Could not send the invitation.");
    }
  }

  return (
    <>
      <InvitationsSection
        subject="seller"
        canEdit={canEdit}
        testId="seller-prequal-invitations"
        rows={adapter.invites.rows}
        loading={adapter.invites.loading}
        error={adapter.invites.error}
        retrying={adapter.invites.retrying}
        onRetry={adapter.invites.refetch}
        resending={adapter.resending}
        onResend={(inviteId) =>
          adapter.resend(inviteId, {
            onSuccess: () => toast.success("Invitation resent."),
            onError: (err) =>
              toast.error(
                (err as { message?: string })?.message
                  ?? "Could not resend the invitation."),
          })
        }
        composer={{
          trigger: !composing && (
            <Button size="sm" variant="secondary" onClick={() => setComposing(true)}>
              <Send className="mr-1.5 h-4 w-4" />
              Invite the seller
            </Button>
          ),
          body: composing && (
            <div className="space-y-2 rounded-md border border-gray-mid/40 p-3">
              <label className="block">
                <span className="text-caption uppercase tracking-wide text-gray-mid">
                  Seller&apos;s email
                </span>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seller@example.com"
                  aria-label="Seller's email address"
                  data-testid="seller-prequal-invite-email"
                  className="mt-space-1"
                />
              </label>
              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  aria-label="Seller's first name"
                />
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name (optional)"
                  aria-label="Seller's last name"
                />
              </div>
              <div className="flex gap-2">
                {/* 🔴 Opens the shared preview; it does NOT send. Nothing here
                    forces an edit — preview and confirm, never a gate. */}
                <Button
                  size="sm"
                  disabled={email.trim().length === 0 || adapter.sending}
                  onClick={() => setPreviewing(true)}
                >
                  Review and send
                </Button>
                <Button
                  size="sm"
                  variant="text"
                  onClick={() => {
                    setComposing(false);
                    setConfirmingDuplicate(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ),
        }}
        duplicate={
          confirmingDuplicate && (
            <SubjectDuplicateConfirm
              subject="seller"
              testId="seller-prequal-invite-duplicate"
              pending={adapter.sending}
              onConfirm={() => void onSend(true)}
              onCancel={() => setConfirmingDuplicate(false)}
            />
          )
        }
      />

      {previewing && (
        <SellerInvitePreview
          email={email.trim()}
          firstName={firstName.trim()}
          lastName={lastName.trim()}
          pending={adapter.sending}
          onClose={() => setPreviewing(false)}
          onConfirm={() => void onSend(false)}
        />
      )}
    </>
  );
}

/**
 * The confirmation before an invitation reaches a seller.
 *
 * <p>🔴 <b>No body, and that is honest rather than lazy.</b> The invitation email
 * is composed inside the prequalification module at send time; this surface has
 * never seen it and there is no draft read to ask. Rendering an invented
 * approximation would be worse than rendering none — a specialist would read it,
 * believe it, and be wrong about what a seller received. What the preview CAN
 * stand behind is who it goes to, what it is for, and where a reply lands.</p>
 *
 * <p>🔴 <b>Replies reach Phoenix, not the sender.</b> This send does not carry a
 * Reply-To: the two buyer send services set one, the prequalification invite path
 * does not. Stating the sender's own address here would be exactly the kind of
 * quiet lie the identity block exists to prevent, so it says the true thing
 * instead.</p>
 */
function SellerInvitePreview({
  email,
  firstName,
  lastName,
  pending,
  onClose,
  onConfirm,
}: {
  email: string;
  firstName: string;
  lastName: string;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const name = [firstName, lastName].filter(Boolean).join(" ");

  return (
    <SendPreviewModal
      title="Invite the seller to prequalify"
      onClose={onClose}
      toName={name || null}
      toEmail={email}
      audienceNote="the seller of this business. It does not go to the buyer or to the listing agent."
      repliesReachPhoenix
      replacesPreviewBody={
        <p className="text-small text-onyx m-0" data-testid="seller-prequal-invite-preview">
          Phoenix will email a single-use link to the seller prequalification
          questionnaire. The invitation carries no figures from any submission
          and says nothing about a buyer — it asks the seller whether they can
          carry a note, and their answers come back to Phoenix.
        </p>
      }
      footnote={
        <>
          The link expires, and the invitation is recorded against this listing so
          every consultant working it can see the seller has been approached.
        </>
      }
      confirmLabel="Send invitation"
      onConfirm={onConfirm}
      pending={pending}
      confirmTestId="seller-prequal-invite-confirm"
    />
  );
}
